/**
 * TEF & True Label: how much of the day's food the body spends digesting (thermic effect), the
 * calories left after that, three ways of counting calories side by side, and a label checker.
 * Information only: TDEE already includes TEF, so nothing here changes the calorie budget.
 */
import { useMemo, useState, type ReactElement } from 'react';
import {
  AGE_BRACKETS, ALL_TEF_CITATIONS, ENERGY_SYSTEMS, ENERGY_SYSTEM_IDS, EVIDENCE_GRADE_HELP, EVIDENCE_GRADE_LABEL, TEF_COEFFICIENTS,
  ageBracketFor, citationUrl, energyKcal,
  type EstimateRange, type EvidenceGrade, type FoodComposition, type TefResult,
} from '@engine/tef';
import { Icon } from '../components/Icon';
import { TrueLabelChecker } from '../components/TrueLabelChecker';
import { Card, Empty, Segmented, Skeleton, StatTile } from '../components/ui';
import { ageOn } from '../lib/dates';
import { MEAL_LABEL } from '../lib/labels';
import { dayComposition, saveTefSettings, tefFor, tefMacrosOfMeal, tefOfMeals, type MealsTef, type TefSettings } from '../lib/tef';
import type { MealLog } from '../lib/types';
import { useApp, useAsync, useDayBurn } from '../state/app';

const r0 = (x: number): string => Math.round(x).toLocaleString();
const rangeText = (r: EstimateRange): string => `${r0(r.low)}–${r0(r.high)}`;
const pct = (x: number): string => `${x.toFixed(1)}%`;

function GradeChip({ grade }: { grade: EvidenceGrade }): ReactElement {
  return <span className={`grade g-${grade.toLowerCase()}`} title={EVIDENCE_GRADE_HELP[grade]}>{EVIDENCE_GRADE_LABEL[grade]}</span>;
}

/** Where the day's TEF comes from: each macro's share of the central estimate. */
function TefSplit({ food, result }: { food: FoodComposition; result: TefResult }): ReactElement {
  const parts: ReadonlyArray<{ name: string; kcal: number; color: string; coef: string }> = [
    { name: 'Protein', kcal: food.proteinG * 4 * TEF_COEFFICIENTS.PROTEIN.range.mid, color: 'var(--protein)', coef: '20–30%' },
    { name: 'Carbs', kcal: food.availableCarbG * 4 * TEF_COEFFICIENTS.CARBOHYDRATE.range.mid, color: 'var(--carbs)', coef: '5–10%' },
    { name: 'Fat', kcal: food.fatG * 9 * TEF_COEFFICIENTS.FAT.range.mid, color: 'var(--fat)', coef: '0–3%' },
    { name: 'Alcohol', kcal: food.alcoholG * 7 * TEF_COEFFICIENTS.ALCOHOL.range.mid, color: 'var(--alcohol)', coef: '10–30%' },
  ];
  const base: number = result.baselineTefKcal.mid;
  return (
    <div className="stack" style={{ gap: 8 }}>
      {parts.filter((p) => p.kcal > 0).map((p) => (
        <div key={p.name} className="wbar">
          <span>{p.name} <span className="faint">({p.coef})</span></span>
          <div className="wbar-track"><b style={{ left: 0, width: `${base > 0 ? (p.kcal / base) * 100 : 0}%`, background: p.color }} /></div>
          <span className="wbar-val num">{r0(p.kcal)} kcal</span>
        </div>
      ))}
    </div>
  );
}

export function TefPage(): ReactElement {
  const { source, profile, day, version, energy, saveSettings, settings: diet, toast } = useApp();
  const { data: meals, error, loading } = useAsync<MealLog[]>(() => source.listMeals(day), [source, day, version]);
  const [local, setLocal] = useState<TefSettings | null>(null);
  const settings: TefSettings = local ?? energy.tefSettings;
  const setSettings = (patch: Partial<TefSettings>): void => {
    const next: TefSettings = { ...settings, ...patch };
    setLocal(next);
    saveTefSettings(next);
    saveSettings({ tef_profile: { ...next } }).catch((err: unknown) => toast(`Couldn't sync this choice: ${String(err)}`, 'err'));
  };

  const list: MealLog[] = useMemo(() => meals ?? [], [meals]);
  const food: FoodComposition = useMemo(() => dayComposition(list), [list]);
  const result: TefResult = useMemo(() => tefFor(food, settings, profile, day), [food, settings, profile, day]);
  const age: number | null = profile.birth_date ? ageOn(profile.birth_date, day) : null;
  const day_: MealsTef = useMemo(() => tefOfMeals(list.map(tefMacrosOfMeal), settings, age), [list, settings, age]);
  const perMeal = useMemo(() => list.map((m) => ({ meal: m, t: tefOfMeals([tefMacrosOfMeal(m)], settings, age) })), [list, settings, age]);
  const { burn } = useDayBurn(day, list);
  const eaten: number = list.reduce((a, m) => a + m.calories, 0);
  const usable: number = Math.max(0, eaten - day_.kcal);
  const bracket = AGE_BRACKETS[ageBracketFor(age)];
  const hasFood: boolean = eaten > 0;
  const inBurn: boolean = diet.energy_mode === 'wearable' && diet.include_tef;

  return (
    <div className="grid cols-main">
      <div className="stack">
        <Card title="Usable calories" sub="what's left after digestion">
          {error !== null && <div className="note err">{error}</div>}
          {loading && meals === null ? <Skeleton height={140} /> : (
            <>
              <div className="tiles">
                <StatTile label="Eaten" value={r0(eaten)} unit="kcal" delta="as logged in your diary" />
                <StatTile label="Spent digesting (TEF)" value={r0(day_.kcal)} unit="kcal" delta={hasFood ? `${rangeText({ low: day_.low, mid: day_.kcal, high: day_.high })} kcal · ${pct((100 * day_.kcal) / eaten)}` : undefined} />
                <StatTile label="Usable" value={r0(usable)} unit="kcal" delta={hasFood ? `${rangeText({ low: Math.max(0, eaten - day_.high), mid: usable, high: Math.max(0, eaten - day_.low) })} kcal` : undefined} deltaClass="good" />
                <StatTile label="FAO net energy" value={r0(result.netMetabolizableKcal)} unit="kcal" delta="official cross-check (from macros)" />
              </div>
              {!hasFood && <Empty title="Nothing logged for this day">Log food in the Diary and its thermic effect shows up here.</Empty>}
              {hasFood && day_.noMacroCalories > 0 && (
                <div className="note warn" style={{ marginTop: 12 }}>
                  {r0(day_.noMacroCalories)} kcal were logged without protein, carbs and fat, so their digestion is counted at the usual 10% of a mixed diet (Westerterp 2004). Log foods with their macros for an exact figure.
                </div>
              )}
              {hasFood && <div style={{ marginTop: 16 }}><TefSplit food={food} result={result} /></div>}
              <div className="note info" style={{ marginTop: 14 }}>
                <Icon name="info" size={15} />{' '}
                {inBurn
                  ? <>In your watch mode, digestion is part of your burn: resting + watch active calories + these {r0(day_.kcal)} kcal{burn.kcal !== null ? ` = ${r0(burn.kcal)} kcal today` : ''}. Turn it off in Targets &amp; Profile.</>
                  : <>Your burn ({diet.energy_mode === 'activity' ? 'resting × activity level' : diet.energy_mode === 'adaptive' ? 'learned from your weight' : 'watch'}) already includes digestion, so this page is information only and nothing is counted twice.</>}
              </div>
            </>
          )}
        </Card>

        <Card title="Three ways to count the same food" sub="today's total">
          <table className="ntable tef-systems">
            <tbody>
              {ENERGY_SYSTEM_IDS.map((id) => {
                const s = ENERGY_SYSTEMS[id];
                return (
                  <tr key={id}>
                    <td><b>{s.displayName}</b><div className="cite">{s.about}</div></td>
                    <td className="num" style={{ textAlign: 'right', whiteSpace: 'nowrap', fontWeight: 800 }}>{r0(energyKcal(food, id))} kcal</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
          <p className="cite" style={{ margin: '8px 0 0' }}>Atwater factors are metabolizable energy (fecal and urinary losses already removed), not gross bomb-calorimeter energy. Labels may use any of five FDA methods.</p>
        </Card>

        <Card title="By meal" sub={`${list.length} ${list.length === 1 ? 'entry' : 'entries'}`}>
          {perMeal.length === 0 ? <Empty title="No meals yet" /> : (
            <table className="ntable tef-table">
              <thead><tr><th>Meal</th><th style={{ textAlign: 'right' }}>Eaten</th><th style={{ textAlign: 'right' }}>TEF</th><th style={{ textAlign: 'right' }}>Usable</th></tr></thead>
              <tbody>
                {perMeal.map(({ meal, t }) => (
                  <tr key={meal.id}>
                    <td><div style={{ fontWeight: 700 }}>{meal.description}</div><div className="cite">{MEAL_LABEL[meal.meal_type]}{t.noMacroCalories > 0 ? ' · no macros, counted at 10%' : ''}</div></td>
                    <td className="num" style={{ textAlign: 'right' }}>{r0(meal.calories)}</td>
                    <td className="num" style={{ textAlign: 'right' }}>{r0(t.kcal)} <span className="faint">({pct(meal.calories > 0 ? (100 * t.kcal) / meal.calories : 0)})</span></td>
                    <td className="num" style={{ textAlign: 'right', fontWeight: 800 }}>{r0(Math.max(0, meal.calories - t.kcal))}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
          <p className="cite" style={{ margin: '8px 0 0' }}>High-protein meals cost the most to digest: about a quarter of protein calories go to processing it.</p>
        </Card>

        <Card title="True Label checker" sub="what a Nutrition Facts panel can really mean">
          <TrueLabelChecker />
        </Card>
      </div>

      <div className="stack">
        <Card title="About you" sub="changes the TEF range">
          <div className="stack" style={{ gap: 14 }}>
            <div className="kv">
              <dt>Age group</dt>
              <dd>{bracket.displayName} {bracket.factor.low === 1 && bracket.factor.high === 1 ? <span className="faint" style={{ fontSize: 12.5 }}>(reference group)</span> : <GradeChip grade={bracket.grade} />}</dd>
            </div>
            {age === null && <div className="note warn">Add your birth date in Targets &amp; Profile to use your age group.</div>}
            <label className="field"><span>Training</span>
              <Segmented value={settings.training} onChange={(v) => setSettings({ training: v })} options={[{ value: 'UNTRAINED', label: 'Untrained' }, { value: 'TRAINED', label: 'Regularly trained' }]} />
            </label>
            <label className="field"><span>Blood sugar</span>
              <Segmented value={settings.metabolicHealth} onChange={(v) => setSettings({ metabolicHealth: v })} options={[{ value: 'NORMAL', label: 'Normal' }, { value: 'INSULIN_RESISTANT', label: 'Insulin resistant / T2D' }]} />
            </label>
            <label className="field"><span>Your meals are mostly</span>
              <Segmented value={settings.mealProcessing} onChange={(v) => setSettings({ mealProcessing: v })} options={[{ value: 'TYPICAL_OR_PROCESSED', label: 'Typical / processed' }, { value: 'WHOLE_FOOD', label: 'Whole foods' }]} />
            </label>
            <label className="field"><span>Evidence</span>
              <Segmented value={settings.mode} onChange={(v) => setSettings({ mode: v })} options={[{ value: 'STRICT', label: 'Strict' }, { value: 'EXPLORATORY', label: 'Exploratory' }]} />
              <small>{settings.mode === 'STRICT' ? 'Only strong and moderate evidence moves the central number; weaker evidence just widens the range.' : 'Limited evidence also moves the central number. Conflicting evidence never does.'}</small>
            </label>
          </div>
          {result.modifiers.length > 0 && (
            <div className="stack" style={{ gap: 10, marginTop: 16 }}>
              {result.modifiers.map(({ modifier: m, affectsCentralEstimate }) => (
                <div key={`${m.kind}:${m.id}`} className="tef-mod">
                  <div className="row" style={{ gap: 8 }}>
                    <b>{m.displayName}</b><GradeChip grade={m.grade} />
                    <span className="faint num" style={{ marginLeft: 'auto', fontSize: 12.5 }}>×{m.factor.low.toFixed(2)}–{m.factor.high.toFixed(2)}</span>
                  </div>
                  <div className="cite" style={{ marginTop: 4 }}>{affectsCentralEstimate ? `Moves the central estimate (×${m.factor.mid.toFixed(2)}).` : 'Only widens the range.'} {m.note}</div>
                </div>
              ))}
            </div>
          )}
        </Card>

        <Card title="The science" sub="where the numbers come from">
          <table className="ntable tef-table">
            <thead><tr><th>Nutrient</th><th style={{ textAlign: 'right' }}>Spent digesting</th><th>Evidence</th></tr></thead>
            <tbody>
              {([['Protein', TEF_COEFFICIENTS.PROTEIN], ['Carbs', TEF_COEFFICIENTS.CARBOHYDRATE], ['Fat', TEF_COEFFICIENTS.FAT], ['Alcohol', TEF_COEFFICIENTS.ALCOHOL]] as const).map(([name, c]) => (
                <tr key={name}><td>{name}</td><td className="num" style={{ textAlign: 'right' }}>{Math.round(c.range.low * 100)}–{Math.round(c.range.high * 100)}%</td><td><GradeChip grade={c.grade} /></td></tr>
              ))}
            </tbody>
          </table>
          <p className="cite" style={{ margin: '8px 0' }}>Fiber and sugar alcohols get no TEF term: their energy is already discounted and there are no specific data for them. No sex adjustment is applied, because nothing peer-reviewed supports one.</p>
          <details>
            <summary className="cite" style={{ cursor: 'pointer', fontWeight: 700 }}>All sources ({ALL_TEF_CITATIONS.length})</summary>
            <ol className="cite" style={{ paddingLeft: 18, margin: '6px 0 0' }}>
              {ALL_TEF_CITATIONS.map((c) => {
                const url: string | null = citationUrl(c);
                return <li key={c.shortRef}><b>{c.shortRef}.</b> {c.title}. {c.finding} {url !== null && <a href={url} target="_blank" rel="noreferrer">link</a>}</li>;
              })}
            </ol>
          </details>
        </Card>
      </div>
    </div>
  );
}
